import { useEffect, useRef } from "react";
import {
  hasUnsavedChanges,
  notifyDirtyChanged,
  registerDirtyChecker,
} from "@/lib/unsavedChangesRegistry";
import { useUnsavedChangesContext } from "@/context/UnsavedChangesProvider";

/**
 * Registers a page or dialog as having unsaved changes.
 * Browser back/forward is intercepted globally via BrowserHistoryGuard.
 * Call `requestLeave` for in-app exits (Back/Cancel buttons, closing a modal)
 * that should reuse the same confirmation dialog.
 */
export function useUnsavedChangesGuard(
  isDirty: boolean | (() => boolean),
) {
  const { requestLeave } = useUnsavedChangesContext();
  const isDirtyRef = useRef(isDirty);
  isDirtyRef.current = isDirty;

  useEffect(() => {
    const checker = () => {
      const value = isDirtyRef.current;
      return typeof value === "function" ? value() : value;
    };

    const unregister = registerDirtyChecker(checker);
    return unregister;
  }, []);

  useEffect(() => {
    notifyDirtyChanged();
  });

  const guardedRequestLeave = (onConfirm: () => void) => {
    if (!hasUnsavedChanges()) {
      onConfirm();
      return;
    }
    requestLeave(onConfirm);
  };

  return { requestLeave: guardedRequestLeave };
}
